import "./TabBar.scss";

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import clsx from "clsx";
import { FC, Fragment, MouseEvent, ReactNode, memo, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { Link } from "../../utils/routing/components/Link";

import { forwardRefComponent } from "mds/utils/components";
import { ComponentEventProperties, addTrackingToEvent } from "utils/amplitude";

export type ComponentTabItem = ComponentEventProperties & {
  label: ReactNode;
  className?: string;
  title: string;
  panel?: ReactNode;
  tabVariant?: string;
  to?: string;
  disabled?: boolean;
  key?: string;
  extraProps?: Record<string, unknown>;
};

export type StringTabItem = ComponentEventProperties & {
  label: string;
  className?: string;
  panel?: ReactNode;
  tabVariant?: string;
  to?: string;
  disabled?: boolean;
  key?: string;
  extraProps?: Record<string, unknown>;
};

export type TabItem = ComponentTabItem | StringTabItem;

type ContentTabBarProps = {
  kind?: "horizontal" | "vertical";
  tabs: TabItem[];
  onChange?: (tabIndex: number) => void;
  selectedIndex?: number;
  contentOnly?: boolean;
  className?: string;
  tabListClassName?: string;
  tabListEnd?: ReactNode;
  skipBackgroundPanelRender?: boolean;
};

/**
 * This is tab bar component used for course content.  It's a simple tab
 * component styled for icons.  Consumers of this component will pass in
 * a list of tabs with labels and panels.
 */
export const TabBar: FC<ContentTabBarProps> = memo(
  ({
    onChange,
    tabs,
    selectedIndex,
    skipBackgroundPanelRender = false,
    contentOnly,
    className,
    tabListClassName,
    tabListEnd,
    kind = "horizontal",
  }) => {
    const getTitle = (tabItem: TabItem) => ("title" in tabItem ? tabItem.title : tabItem.label);
    // TODO: Didn't rename "primary" to "horizontal" yet because the name is entangled in other stylesheets
    const baseClass = kind === "horizontal" ? "tab-bar-primary" : "tab-bar-vertical";

    const onTabClick = (
      e: MouseEvent<HTMLAnchorElement>,
      tabItem: TabItem,
      { eventName, eventProperties }: ComponentEventProperties,
    ) => {
      let onEvent: () => void | undefined;

      if (!tabItem.to) {
        e.preventDefault();

        if (onChange) {
          onEvent = () => onChange(tabs.indexOf(tabItem));
        }
      }

      // eslint-disable-next-line strict-null-checks/all
      addTrackingToEvent({ eventName, eventProperties, onEvent })(e);
    };

    const tabComponents = useMemo(
      () =>
        tabs.map((tabItem) => ({
          TabComponent: forwardRefComponent(() => tabItem.label),
          PanelComponent: forwardRefComponent(() => tabItem.panel, {
            className: `${baseClass}-panel w-full h-full`,
          }),
        })),
      [tabs, baseClass],
    );

    const tabItems = tabs.map((tabItem, index) => {
      const title = getTitle(tabItem);
      const { TabComponent } = tabComponents[index];
      const eventName = tabItem.eventName;
      const eventProperties = tabItem.eventProperties;
      const extraProps = tabItem.extraProps || {};
      const tabProps = {
        as: Fragment,
        className: clsx("tab group/tab -mb-0.5 disabled:text-black-tint-55", tabItem.className),
        disabled: tabItem.disabled,
        title,
        to: "",
        onClick: (e) => onTabClick(e, tabItem, { eventName, eventProperties }),
      };

      if (tabItem.to) {
        tabProps.as = Link;
        tabProps.to = tabItem.to;
      }

      return (
        <Tab key={tabItem.key || title} {...tabProps} {...extraProps}>
          <TabComponent />
        </Tab>
      );
    });

    const tabPanels = tabs.map((tabItem, panelIndex) => {
      const { PanelComponent } = tabComponents[panelIndex];

      // We avoid rendering panels that aren't selected, instead of just hiding them.
      // This is important for performance (less rendering on large items), and
      // so that block IDs are uniquely scrollable.
      if (skipBackgroundPanelRender && panelIndex !== selectedIndex) {
        return null;
      }

      return (
        <TabPanel as={Fragment} key={tabItem.key || getTitle(tabItem)} tabIndex={-1}>
          <PanelComponent />
        </TabPanel>
      );
    });

    return (
      <TabGroup
        className={clsx(baseClass, className, { "content-only": contentOnly })}
        selectedIndex={selectedIndex}
        onChange={onChange}
      >
        <div className="flex w-full items-center justify-between overflow-x-auto sm:overflow-x-visible">
          <TabList
            className={twMerge(
              `${baseClass}-list grow items-center`,
              contentOnly && "!hidden",
              kind === "horizontal" && "gap-x-8",
              tabListClassName,
            )}
          >
            {tabItems}
          </TabList>

          {tabListEnd}
        </div>

        <TabPanels className={`${baseClass}-content`}>{tabPanels}</TabPanels>
      </TabGroup>
    );
  },
  (prevProps, nextProps) =>
    (["tabs", "selectedIndex", "contentOnly"] as const).every(
      (propName) => prevProps[propName] === nextProps[propName],
    ),
);

TabBar.displayName = "TabBar";
